import { useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>(".ea0d8f2b_hp input");
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      return;
    }

    setStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8v8pts6ci2plld1o4mg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  const mainCategories = categories.slice(0, 5);
  const moreCategories = categories.slice(5, 9);
  const resourceLinks = [
    { label: t("footer.link_blog"), href: "/artigo/guia-completo-hoobuy-spreadsheet" },
    { label: t("footer.link_safety"), href: "#" },
    { label: t("footer.link_shipping"), href: "#" },
    { label: t("footer.link_contact"), href: "#" },
  ];
  const topLinks = [
    { label: "Hoobuy Sheet", href: "https://kaspreadsheet.com" },
    { label: "Buy It Now", href: "https://kaspreadsheet.com" },
    { label: "Spreadsheet", href: "https://kaspreadsheet.com" },
    { label: "Kasp Spreadsheet", href: "https://kaspreadsheet.com" },
  ];

  return (
    <footer className="w-full bg-accent-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="w-full h-full" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, oklch(0.98 0.01 145) 1px, transparent 1px), radial-gradient(circle at 70% 60%, oklch(0.98 0.01 145) 1px, transparent 1px), radial-gradient(circle at 40% 80%, oklch(0.98 0.01 145) 1px, transparent 1px)",
          backgroundSize: "60px 60px, 80px 80px, 50px 50px",
        }}></div>
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
          <div className="lg:col-span-2">
            <a href="/" className="inline-block mb-4">
              <span className="font-heading text-2xl font-bold text-background-50 tracking-tight">
                Hoobuy<span className="text-primary-400">.</span>Lifestyle
              </span>
            </a>
            <p className="text-sm text-background-50/60 leading-relaxed max-w-sm mb-6">
              {t("footer.brand_tagline")}
            </p>

            <div>
              <p className="text-xs font-semibold text-background-50/80 uppercase tracking-wider mb-3">
                {t("footer.newsletter_title")}
              </p>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex gap-0 max-w-sm"
              >
                <div className="ea0d8f2b_hp">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.newsletter_placeholder")}
                  required
                  className="flex-1 px-4 py-3 text-sm bg-background-50/10 border border-background-50/20 rounded-l-full text-background-50 placeholder:text-background-50/40 focus:outline-none focus:border-background-50/50 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-r-full hover:bg-primary-400 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    t("footer.newsletter_button")
                  )}
                </button>
              </form>
              {status === "success" && (
                <p className="text-xs text-accent-300 mt-2">{t("footer.newsletter_success")}</p>
              )}
              {status === "error" && (
                <p className="text-xs text-primary-300 mt-2">{t("footer.newsletter_error")}</p>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-background-50 mb-4">{t("footer.column1_title")}</p>
            <div className="flex flex-col gap-2.5">
              <a href="/" className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">{t("footer.link_home")}</a>
              <a href="#sobre" className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">{t("footer.link_about")}</a>
              <a href="#faq" className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">{t("footer.link_faq")}</a>
              <a href="/artigo/guia-completo-hoobuy-spreadsheet" className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">{t("footer.link_blog")}</a>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-background-50 mb-4">{t("footer.column2_title")}</p>
            <div className="flex flex-col gap-2.5">
              {mainCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.namePt}
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold text-background-50 mb-4">{t("footer.column3_title")}</p>
            <div className="flex flex-col gap-2.5">
              {moreCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.namePt}
                </a>
              ))}
              {resourceLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm text-background-50/55 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-background-50/10 gap-4">
          <p className="text-xs text-background-50/40">
            {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-4">
            <a href="https://x.com" target="_blank" rel="noopener nofollow" className="w-9 h-9 rounded-lg border border-background-50/20 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/40 transition-all cursor-pointer">
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener nofollow" className="w-9 h-9 rounded-lg border border-background-50/20 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/40 transition-all cursor-pointer">
              <i className="ri-instagram-line text-sm"></i>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener nofollow" className="w-9 h-9 rounded-lg border border-background-50/20 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/40 transition-all cursor-pointer">
              <i className="ri-youtube-line text-sm"></i>
            </a>
            <a href="https://discord.com" target="_blank" rel="noopener nofollow" className="w-9 h-9 rounded-lg border border-background-50/20 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/40 transition-all cursor-pointer">
              <i className="ri-discord-line text-sm"></i>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors whitespace-nowrap cursor-pointer">
              {t("footer.privacy")}
            </a>
            <a href="#" className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors whitespace-nowrap cursor-pointer">
              {t("footer.terms")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}